import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Camera, Plus, Stamp, Trash2, TriangleAlert } from 'lucide-react';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, Select, Textarea } from '../../components/ui/field.js';
import { Badge, Card, CardBody, CardHeader, CardTitle, Tooltip } from '../../components/ui/primitives.js';
import { MASTER_DEFINITIONS } from '../../components/layout/navigation.js';
import { MasterFormDialog } from '../masters/MasterFormDialog.js';
import { useMasterOptions, useMould, type CatalogMasterTable } from '../../db/queries.js';
import { db } from '../../db/schema.js';
import { createRow, updateRow } from '../../db/mutations.js';
import { MOULD_FIELDS, MOULD_SECTIONS, type FieldDef } from './fields.js';
import { fromDateInput, toDateInput, formatBytes } from '../../lib/format.js';
import { dataUrlBytes, fileToDataUrl } from '../../lib/image.js';
import { cn } from '../../lib/cn.js';

type Values = Record<string, string>;

/**
 * Create and edit, one component.
 *
 * The two differ by exactly three things - the title, the initial values and
 * whether the mould ID is editable - which is not enough to justify two screens
 * that will drift apart.
 */
export function MouldFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { assetCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { cloneOf?: string } | null;
  // "Add" beside a blank detail on the mould page opens the form at that field.
  const focusField = new URLSearchParams(location.search).get('field');
  const existing = useMould(mode === 'edit' ? assetCode : undefined);

  const [values, setValues] = useState<Values>({ type: 'MOULD' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** Which master table the "＋ New" dialog is currently creating into. */
  const [quickCreate, setQuickCreate] = useState<{ table: CatalogMasterTable; field: string } | null>(
    null,
  );

  // Populate once the row arrives from Dexie.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== 'edit' || !existing || loadedFor.current === existing.id) return;
    loadedFor.current = existing.id;
    const next: Values = {};
    for (const section of MOULD_SECTIONS) {
      for (const field of section.fields) {
        const raw = (existing as unknown as Record<string, unknown>)[field.name];
        next[field.name] =
          raw === null || raw === undefined
            ? ''
            : field.kind === 'date'
              ? toDateInput(String(raw))
              : String(raw);
      }
    }
    setValues(next);
  }, [existing, mode]);

  // Once the values are in, take the cursor to the field that was asked for.
  const focusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!focusField || !existing || focusedFor.current === focusField) return;
    focusedFor.current = focusField;
    requestAnimationFrame(() => {
      const box = document.querySelector<HTMLElement>(`[data-field="${CSS.escape(focusField)}"]`);
      box?.scrollIntoView({ block: 'center' });
      box?.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
    });
  }, [focusField, existing]);

  /**
   * Creating from an existing mould: copy the spec, drop the identity.
   *
   * The code, the S.NO and the photo are deliberately not copied. Two moulds
   * cannot share a code, the numbering is per-tool, and a photo of the wrong
   * tool is worse than no photo - it makes the duplicate look finished when it
   * is not.
   */
  const clonedFrom = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== 'create' || !routeState?.cloneOf || clonedFrom.current === routeState.cloneOf) {
      return;
    }
    clonedFrom.current = routeState.cloneOf;
    void db.moulds.get(routeState.cloneOf).then((source) => {
      if (!source) return;
      const next: Values = {};
      for (const field of MOULD_FIELDS) {
        // The QR number is this tool's own, like its code: never copied to another.
        if (field.name === 'assetCode' || field.name === 'catalogNo' || field.name === 'photoUrl' || field.name === 'qrNumber') {
          continue;
        }
        const raw = (source as unknown as Record<string, unknown>)[field.name];
        next[field.name] =
          raw === null || raw === undefined
            ? ''
            : field.kind === 'date'
              ? toDateInput(String(raw))
              : String(raw);
      }
      next.name = `${String(source.name ?? '')} (copy)`.trim();
      setValues((v) => ({ ...v, ...next }));
    });
  }, [mode, routeState?.cloneOf]);

  // Browsers only honour this if the user has interacted with the page, which is
  // exactly the case we care about: half-filled form, accidental back gesture.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  function set(name: string, value: string) {
    setValues((v) => ({ ...v, [name]: value }));
    setDirty(true);
    setErrors((e) => (e[name] ? { ...e, [name]: '' } : e));
  }

  async function onSubmit() {
    const nextErrors: Record<string, string> = {};
    if (!values.name?.trim()) nextErrors.name = 'A mould name is required.';

    const code = values.assetCode?.trim();
    if (mode === 'create' && !code && !values.projectId) {
      nextErrors.assetCode = 'Enter a mould ID, or pick a project to generate one from.';
    }
    if (code) {
      const clash = await db.moulds.where('assetCode').equalsIgnoreCase(code).first();
      if (clash && clash.id !== existing?.id) {
        nextErrors.assetCode = `Mould ID "${code}" already exists.`;
      }
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      // Send focus to the first problem rather than leaving it at the button.
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }

    const payload = buildPayload(values);

    // S.NO keeps the list numbered the way the spreadsheet is. Nobody should
    // have to scroll to the bottom of the catalog to find out what comes next,
    // so a blank one is filled in here - and it stays editable, because the
    // client's own numbering has gaps they may want to preserve.
    if (mode === 'create' && payload.catalogNo == null) {
      const all = await db.moulds.toArray();
      payload.catalogNo = all.reduce((n, m) => Math.max(n, m.catalogNo ?? 0), 0) + 1;
    }

    setSaving(true);
    try {
      if (mode === 'edit' && existing) {
        await updateRow('moulds', existing.id, payload);
        setDirty(false);
        navigate(`/moulds/${encodeURIComponent(existing.assetCode)}`);
      } else {
        // Offline, the server cannot allocate a code yet - a readable placeholder
        // keeps the row identifiable in the list until sync replaces it.
        const provisional = code || `NEW-${Date.now().toString(36).toUpperCase().slice(-5)}`;
        // `status` matches the server's own default rather than being left
        // undefined until the first sync. A new tool is on the shelf: nobody has
        // taken it anywhere. Leaving it unset made the local row disagree with
        // its own type and show a hole where every other mould shows a state.
        await createRow('moulds', {
          ...payload,
          assetCode: provisional,
          isActive: true,
          status: 'IN_STORE',
        });
        setDirty(false);
        navigate(`/moulds/${encodeURIComponent(provisional)}`);
      }
    } finally {
      setSaving(false);
    }
  }

  const title = mode === 'create' ? 'New mould' : `Edit ${existing?.assetCode ?? ''}`;

  return (
    <Page
      title={title}
      description={
        mode === 'create'
          ? 'Specification for a new mould. Everything can be edited later except the mould ID once engraved.'
          : existing?.name
      }
      back={
        mode === 'edit' && existing
          ? { to: `/moulds/${encodeURIComponent(existing.assetCode)}`, label: 'Back to mould' }
          : { to: '/moulds', label: 'Back to catalog' }
      }
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={saving}>
            {mode === 'create' ? 'Create mould' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        {MOULD_SECTIONS.map((section) => (
          <Card key={section.id} className={section.id === 'identity' ? 'lg:col-span-2' : undefined}>
            <CardHeader>
              <div>
                <CardTitle>{section.title}</CardTitle>
                {section.description ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">{section.description}</p>
                ) : null}
              </div>
            </CardHeader>
            <CardBody>
              <div
                className={cn(
                  'grid gap-4',
                  section.id === 'identity' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2',
                )}
              >
                {section.fields.map((field) => (
                  <div
                    key={field.name}
                    data-field={field.name}
                    className={field.wide ? 'sm:col-span-2 lg:col-span-3' : undefined}
                  >
                    <FormField
                      field={field}
                      value={values[field.name] ?? ''}
                      error={errors[field.name]}
                      disabled={mode === 'edit' && field.name === 'assetCode'}
                      onChange={(v) => set(field.name, v)}
                      onQuickCreate={(table) => setQuickCreate({ table, field: field.name })}
                    />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Stamp className="size-3.5 shrink-0 text-primary" aria-hidden />
        Fields marked <span className="font-medium text-primary">engraved</span> are cut into the
        metal nameplate. Check them before the plate is made.
      </p>

      {quickCreate ? (
        <MasterFormDialog
          definition={MASTER_DEFINITIONS.find((d) => d.table === quickCreate.table)!}
          open
          onOpenChange={(open) => !open && setQuickCreate(null)}
          onCreated={(id) => set(quickCreate.field, id)}
        />
      ) : null}
    </Page>
  );
}

function FormField({
  field,
  value,
  error,
  disabled,
  onChange,
  onQuickCreate,
}: {
  field: FieldDef;
  value: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onQuickCreate: (table: CatalogMasterTable) => void;
}) {
  const options = useMasterOptions((field.master ?? 'companies') as CatalogMasterTable);

  const label = (
    <span className="inline-flex items-center gap-1.5">
      {field.label}
      {field.onNameplate ? (
        <Tooltip content="This value is engraved on the physical nameplate">
          <Badge tone="primary" className="cursor-help">
            engraved
          </Badge>
        </Tooltip>
      ) : null}
    </span>
  );

  if (field.kind === 'master') {
    return (
      <Field label={label} hint={field.hint} error={error}>
        <div className="flex gap-2">
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Not set"
            options={options ?? []}
            disabled={disabled}
            className="flex-1"
          />
          <Tooltip content={`Add a new ${field.label.toLowerCase()}`}>
            <Button
              variant="secondary"
              size="icon"
              type="button"
              onClick={() => onQuickCreate(field.master!)}
              aria-label={`Add a new ${field.label.toLowerCase()}`}
            >
              <Plus className="size-4" />
            </Button>
          </Tooltip>
        </div>
      </Field>
    );
  }

  if (field.kind === 'yesno') {
    return (
      <Field label={label} hint={field.hint} error={error}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Not set"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          disabled={disabled}
        />
      </Field>
    );
  }

  if (field.kind === 'select') {
    return (
      <Field label={label} hint={field.hint} error={error}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={field.options ?? []}
          disabled={disabled}
        />
      </Field>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <Field label={label} hint={field.hint} error={error}>
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      </Field>
    );
  }

  if (field.kind === 'photo') {
    return <PhotoField label={field.label} value={value} onChange={onChange} />;
  }

  return (
    <Field label={label} hint={field.hint} error={error} required={field.required}>
      <Input
        type={field.kind === 'number' ? 'number' : field.kind === 'date' ? 'date' : 'text'}
        inputMode={field.kind === 'number' ? 'numeric' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
      />
    </Field>
  );
}

function PhotoField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [tooBig, setTooBig] = useState(false);
  const size = useMemo(() => (value.startsWith('data:') ? dataUrlBytes(value) : 0), [value]);

  return (
    <Field
      label={label}
      hint="Stored on this device immediately and uploaded on the next sync."
    >
      <div className="flex items-start gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="size-20 rounded-lg border border-border object-cover"
          />
        ) : (
          <div className="grid size-20 place-items-center rounded-lg border border-dashed border-border-strong text-subtle-foreground">
            <Camera className="size-5" aria-hidden />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            // `capture` opens the camera directly on a phone - the common case
            // for someone standing next to the mould.
            capture="environment"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setBusy(true);
              setTooBig(false);
              try {
                const dataUrl = await fileToDataUrl(file);
                setTooBig(dataUrlBytes(dataUrl) > 800_000);
                onChange(dataUrl);
              } finally {
                setBusy(false);
                e.target.value = '';
              }
            }}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={busy}
              onClick={() => inputRef.current?.click()}
            >
              <Camera className="size-3.5" /> {value ? 'Replace' : 'Add photo'}
            </Button>
            {value ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
                <Trash2 className="size-3.5" /> Remove
              </Button>
            ) : null}
          </div>
          {size ? (
            <p className="text-xs text-muted-foreground">Resized to {formatBytes(size)}</p>
          ) : null}
          {tooBig ? (
            <p className="flex items-center gap-1.5 text-xs text-warning">
              <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
              Large photo - it will sync slowly on a weak connection.
            </p>
          ) : null}
        </div>
      </div>
    </Field>
  );
}

/** Form strings → typed payload. Blank means "not set", never empty string. */
function buildPayload(values: Values): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const section of MOULD_SECTIONS) {
    for (const field of section.fields) {
      const raw = (values[field.name] ?? '').trim();
      if (field.name === 'assetCode') continue; // handled by the caller

      if (field.kind === 'number') {
        payload[field.name] = raw === '' ? null : Number(raw);
      } else if (field.kind === 'yesno') {
        payload[field.name] = raw === '' ? null : raw === 'true';
      } else if (field.kind === 'date') {
        payload[field.name] = fromDateInput(raw);
      } else {
        payload[field.name] = raw === '' ? null : raw;
      }
    }
  }
  return payload;
}
